'use client'

import { useTranslations } from 'next-intl'
import Section from '@/kmsApp/components/layout/section'
import Itineraire from '@/kmsApp/components/itineraire'
import Button from '@/kmsApp/components/shared/ui/button'
import { Control, FieldErrors, UseFormRegister, useWatch } from 'react-hook-form'
import { CourseForm as CourseFormType } from '@/kmsApp/types/forms/course-form'
import { Adresse } from '@/kmsApp/types/models'
import { Select } from '@/kmsApp/components/forms/select'
import { PaymentType, buildPaymentTypeLabels } from '@/kmsApp/types/records/type-payment-record'
import { CourseEstimateResult } from '@/kmsApp/types/course-estimate'
import { REQUIRED } from '@/kmsApp/constants/regex-const'

interface Props {
  control: Control<CourseFormType>
  register: UseFormRegister<CourseFormType>
  errors: FieldErrors<CourseFormType>
  itinerarySteps: {
    type: string
    label: string
    name: string
    adresse: Adresse
  }[]
  prevStep: () => void
  canUseFacture: boolean
  paiementABordAutorise: boolean
  isSending: boolean
  estimate?: CourseEstimateResult | null
  estimateLoading?: boolean
  estimateError?: string | null
  /** Prix convenu quand la course suit un trajet préconfiguré : il remplace l'estimation. */
  prixNegocie?: number | null
  libelleTarifFixe?: string | null
  serviceName?: string | null
}

export const ItineraryRecap = ({
  control,
  register,
  itinerarySteps,
  errors,
  prevStep,
  canUseFacture,
  paiementABordAutorise,
  isSending,
  estimate,
  estimateLoading,
  estimateError,
  prixNegocie = null,
  libelleTarifFixe = null,
  serviceName = null,
}: Props) => {
  const t = useTranslations('web_course_form')
  const tCommon = useTranslations('common')
  const tEnums = useTranslations('enums')
  const paymentTypeLabel = buildPaymentTypeLabels(tEnums)
  const paymentType = useWatch({ control, name: `paymentType` })

  return (
    <div className="">
      <Section titre={t('itinerary')} icon="map">
        {/* Trajet préconfiguré : le prix est convenu d'avance, il n'y a ni
            itinéraire à recomposer ni estimation à afficher. */}
        {prixNegocie !== null ? (
          <div className="rounded-xl border border-primary/20 bg-primary/5 dark:bg-white/10 dark:border-white p-4 md:p-5">
            <p className="text-sm font-bold uppercase tracking-widest text-primary dark:text-white">
              {t('agreed_price')} {libelleTarifFixe ? `— ${libelleTarifFixe}` : ''}
            </p>
            <p className="text-2xl! md:text-3xl! font-bold text-slate-900 dark:text-white">
              {new Intl.NumberFormat('fr-BE', {
                style: 'currency',
                currency: 'EUR',
              }).format(prixNegocie)}
            </p>
          </div>
        ) : (
          itinerarySteps &&
          itinerarySteps.length >= 2 && (
            <Itineraire
              itinerarySteps={itinerarySteps}
              estimate={estimate}
              estimateLoading={estimateLoading}
              estimateError={estimateError}
              serviceName={serviceName}
            />
          )
        )}

        <Select
          control={control}
          titre={t('payment_method')}
          placeholder={t('select_payment_method')}
          error={errors?.paymentType?.message}
          options={[
            ...Object.values(PaymentType)
              .filter((v) => v !== PaymentType.Facture || canUseFacture)
              .filter((v) => v !== PaymentType.FactureRetail)
              .map((v) => ({
                value: v,
                label: paymentTypeLabel[v] ?? '',
                disabled: v === PaymentType.ABord && !paiementABordAutorise,
                hint:
                  v === PaymentType.ABord && !paiementABordAutorise
                    ? t('payment_abord_unavailable_hint')
                    : undefined,
              })),
          ]}
          {...register('paymentType', {
            required: REQUIRED(tCommon).required,
            validate: (v: number) => v !== -1 || REQUIRED(tCommon).required,
            valueAsNumber: true,
          })}
          containerClassName="sm:col-span-2"
        />

        <div className="mt-8 flex flex-col md:flex-row gap-2 justify-end">
          <Button type="button" variant="secondary" onClick={() => prevStep()}>
            {t('back')}
          </Button>

          <Button
            icon="save"
            variant="primary"
            type="submit"
            className="flex-1 md:flex-none disabled:bg-gray-400"
            disabled={isSending}
          >
            {isSending
              ? t('sending')
              : paymentType == PaymentType.Stripe
                ? t('save_and_pay')
                : t('book_ride')}
          </Button>
        </div>
      </Section>
    </div>
  )
}
